// angular
import { NgClass } from '@angular/common';
import { Component, effect, inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
// components
import { ButtonComponent } from '@app/core/components/button/button.component';
import { InputComponent } from '@app/core/components/input/input.component';
import { ToggleSwitchComponent } from '@app/core/components/toggle-switch/toggle-switch.component';
// services
import { RecaptchaService } from '@app/shared/services/recaptcha.service';
// types
import {
  IProjectQuotePayload,
  ProjectQuoteMemberType,
} from '@app/+projects/shared/types/project-quote.interface';
import { RecaptchaData } from '@app/shared/types/recaptcha-data.type';
// utils
import { DialogRef } from '@app/core/shared/utils/dialog/dialog-ref';
import { DIALOG_DATA } from '@app/core/shared/utils/dialog/dialog-tokens';
// enums & pipes
import { Roles } from '@app/shared/enums/roles.enum';
import { HasAccessPipe } from '@app/shared/pipes/has-access.pipe';
import { environment } from '@environments/environment';

export interface ProjectQuoteDialogData {
  title: string;
  memberType: ProjectQuoteMemberType;
}

@Component({
  selector: 'pwj-project-quote-dialog',
  standalone: true,
  imports: [
    InputComponent,
    ToggleSwitchComponent,
    ReactiveFormsModule,
    FormsModule,
    ButtonComponent,
    TranslateModule,
    NgClass,
  ],
  templateUrl: './project-quote-dialog.component.html',
  styleUrl: './project-quote-dialog.component.scss',
})
export class ProjectQuoteDialogComponent implements OnInit {
  public quoteForm!: FormGroup;
  public formIsSubmitting = false;
  public isDisabledSubmitBtn = false;
  public hasAccessToCreateQuote = false;
  public dialogData!: ProjectQuoteDialogData;
  public isNegotiable = false;

  private readonly _hasAccessPipe = inject(HasAccessPipe);
  private readonly _fb = inject(FormBuilder);
  private readonly _dialogRef = inject(DialogRef);
  public readonly data: ProjectQuoteDialogData = inject(DIALOG_DATA);
  private readonly _recaptchaService = inject(RecaptchaService);

  constructor() {
    this._recaptchaService.recaptchaValidation('project_quote_form');
    this.dialogData = this.data;
    this.hasAccessToCreateQuote = this._hasAccessPipe.transform(
      Roles.CreateProjectQuote
    );

    effect(() => {
      if (this._recaptchaService.recaptchaData() !== null) {
        const captchaData =
          this._recaptchaService.recaptchaData() as RecaptchaData;

        if (captchaData?.score < environment.recaptcha.targetScore) {
          this.isDisabledSubmitBtn = true;
        }
      }
    });
  }

  ngOnInit(): void {
    this.quoteForm = this._fb.group({
      estimated_time: [null, [Validators.required, Validators.min(1)]],
      is_negotiable: [false],
      total_amount: [null, Validators.required],
      description: [''],
    });

    this.applyAmountValidators(this.quoteForm.get('is_negotiable')?.value === true);

    this.quoteForm.get('is_negotiable')?.valueChanges.subscribe(isNegotiable => {
      this.isNegotiable = !!isNegotiable;
      this.applyAmountValidators(!!isNegotiable);
    });

    this.quoteForm.valueChanges.subscribe(() => {
      if (!this.quoteForm.dirty) {
        this.isDisabledSubmitBtn = true;
      }
    });
  }

  public onNegotiableToggle(checked: boolean): void {
    this.quoteForm.patchValue({ is_negotiable: checked });
    this.quoteForm.markAsDirty();
  }

  public close(): void {
    this._dialogRef.close();
  }

  public submitQuote(): void {
    this.quoteForm.markAllAsTouched();
    if (this.quoteForm.invalid || !this.quoteForm.dirty) {
      return;
    }

    const { estimated_time, is_negotiable, total_amount, description } =
      this.quoteForm.value;

    const payload: IProjectQuotePayload = {
      member_type: this.dialogData.memberType,
      estimated_time: Number(estimated_time),
      is_negotiable: !!is_negotiable,
      description: description?.trim() ? description.trim() : undefined,
    };

    if (!payload.is_negotiable && total_amount != null && total_amount !== '') {
      payload.total_amount = Number(total_amount);
    }

    this._dialogRef.close(payload);
  }

  private applyAmountValidators(isNegotiable: boolean): void {
    const control = this.quoteForm.get('total_amount');
    if (!control) return;

    if (isNegotiable) {
      control.clearValidators();
      control.setValue(null);
    } else {
      control.setValidators([Validators.required, Validators.min(1)]);
    }
    control.updateValueAndValidity();
  }

}
